/**
 * WebSocket Service for Real-time Communication
 * 
 * Handles WebSocket connections, message broadcasting, typing indicators,
 * presence updates, and real-time notifications.
 */

import { Auth } from 'aws-amplify';

// WebSocket configuration
const WS_BASE_URL = 'wss://4vwnjb0iy5.execute-api.ap-south-1.amazonaws.com/prod';

// Event types
export const WS_EVENT_TYPES = {
  MESSAGE: 'message',
  TYPING_START: 'typing_start',
  TYPING_STOP: 'typing_stop',
  PRESENCE_UPDATE: 'presence_update',
  MESSAGE_READ: 'message_read',
  CONNECTION_ACK: 'connection_ack',
  ERROR: 'error'
};

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.eventListeners = new Map();
    this.messageQueue = [];
    this.userId = null;
    this.heartbeatInterval = null;
    this.connectionPromise = null;
  }

  /**
   * Initialize WebSocket connection
   */
  async connect() {
    if (this.isConnected || this.isConnecting) {
      return this.connectionPromise;
    }

    this.isConnecting = true;
    this.connectionPromise = this._performConnect();
    return this.connectionPromise;
  }

  async _performConnect() {
    try {
      // Get current user session for authentication
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      this.userId = session.getIdToken().payload.sub;

      // Create WebSocket connection with authentication
      const wsUrl = `${WS_BASE_URL}?token=${token}&userId=${this.userId}`;
      
      console.log('Connecting to WebSocket:', wsUrl.replace(token, '[TOKEN]'));
      
      this.ws = new WebSocket(wsUrl);
      
      // Set up event handlers
      this.ws.onopen = this._handleOpen.bind(this);
      this.ws.onmessage = this._handleMessage.bind(this);
      this.ws.onclose = this._handleClose.bind(this);
      this.ws.onerror = this._handleError.bind(this);

      // Wait for connection to be established
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          this._handleOpen();
          resolve();
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeout);
          this._handleError(error);
          reject(error);
        };
      });
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      this.isConnecting = false;
      throw error;
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    console.log('WebSocket disconnected');
  }

  /**
   * Send message through WebSocket
   */
  async send(action, data) {
    if (!this.isConnected) {
      // Queue message if not connected
      this.messageQueue.push({ action, data });
      
      // Try to reconnect
      try {
        await this.connect();
      } catch (error) {
        console.error('Failed to connect for sending message:', error);
        throw error;
      }
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = {
        action,
        ...data
      };
      
      console.log('Sending WebSocket message:', message);
      this.ws.send(JSON.stringify(message));
    } else {
      throw new Error('WebSocket not connected');
    }
  }

  /**
   * Send a chat message
   */
  async sendMessage(conversationId, content, type = 'text', attachments = []) {
    return this.send('sendMessage', {
      conversationId,
      content,
      type,
      attachments
    });
  }

  /**
   * Send typing indicator
   */
  async sendTyping(conversationId, isTyping) {
    return this.send('typing', {
      conversationId,
      isTyping
    });
  }

  /**
   * Mark messages as read
   */
  async markRead(conversationId, messageIds = []) {
    return this.send('markRead', {
      conversationId,
      messageIds
    });
  }

  /**
   * Update presence status
   */
  async updatePresence(status = 'online') {
    return this.send('presence', {
      status
    });
  }

  /**
   * Add event listener
   */
  addEventListener(eventType, callback) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    this.eventListeners.get(eventType).add(callback);
  }

  /**
   * Remove event listener
   */
  removeEventListener(eventType, callback) {
    if (this.eventListeners.has(eventType)) {
      this.eventListeners.get(eventType).delete(callback);
    }
  }

  /**
   * Remove all event listeners for a type
   */
  removeAllEventListeners(eventType) {
    if (eventType) {
      this.eventListeners.delete(eventType);
    } else {
      this.eventListeners.clear();
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      userId: this.userId
    };
  }

  /**
   * Private event handlers
   */
  _handleOpen() {
    console.log('WebSocket connected');
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;

    // Process queued messages
    while (this.messageQueue.length > 0) {
      const { action, data } = this.messageQueue.shift();
      this.send(action, data).catch(console.error);
    }

    // Start heartbeat
    this._startHeartbeat();

    // Emit connection event
    this._emitEvent('connected', { userId: this.userId });
  }

  _handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      console.log('WebSocket message received:', data);

      // Update activity on any message
      this._updateActivity();

      // Handle different message types
      switch (data.type) {
        case WS_EVENT_TYPES.CONNECTION_ACK:
          console.log('Connection acknowledged');
          break;

        case WS_EVENT_TYPES.MESSAGE:
          this._emitEvent('message', data.message);
          break;

        case WS_EVENT_TYPES.TYPING_START:
          this._emitEvent('typing_start', {
            conversationId: data.conversationId,
            userId: data.userId
          });
          break;

        case WS_EVENT_TYPES.TYPING_STOP:
          this._emitEvent('typing_stop', {
            conversationId: data.conversationId,
            userId: data.userId
          });
          break;

        case WS_EVENT_TYPES.PRESENCE_UPDATE:
          this._emitEvent('presence_update', {
            userId: data.userId,
            status: data.status,
            timestamp: data.timestamp
          });
          break;

        case WS_EVENT_TYPES.MESSAGE_READ:
          this._emitEvent('message_read', {
            conversationId: data.conversationId,
            readBy: data.readBy,
            messageIds: data.messageIds,
            timestamp: data.timestamp
          });
          break;

        case WS_EVENT_TYPES.ERROR:
          console.error('WebSocket error message:', data.error);
          this._emitEvent('error', { error: data.error, message: data.message });
          break;

        default:
          console.log('Unknown WebSocket message type:', data.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  _handleClose(event) {
    console.log('WebSocket closed:', event.code, event.reason);
    this.isConnected = false;
    this.isConnecting = false;

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // Emit disconnect event
    this._emitEvent('disconnected', { 
      code: event.code, 
      reason: event.reason,
      userId: this.userId 
    });

    // Auto-reconnect if not a clean disconnect
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this._scheduleReconnect();
    }
  }

  _handleError(error) {
    console.error('WebSocket error:', error);
    this._emitEvent('error', { error: error.message || 'WebSocket error' });
  }

  _scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (!this.isConnected && !this.isConnecting) {
        this.connect().catch(console.error);
      }
    }, delay);
  }

  _startHeartbeat() {
    // Send ping every 30 seconds to keep connection alive
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000);
  }

  _updateActivity() {
    // Could be used to update last activity timestamp
    // for presence management
  }

  _emitEvent(eventType, data) {
    if (this.eventListeners.has(eventType)) {
      this.eventListeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${eventType}:`, error);
        }
      });
    }
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

// Auto-connect when user is authenticated
let autoConnectInitialized = false;

export const initializeWebSocket = async () => {
  if (autoConnectInitialized) return;
  autoConnectInitialized = true;

  try {
    // Check if user is authenticated
    await Auth.currentSession();
    
    // Connect to WebSocket
    await websocketService.connect();
    
    // Handle authentication state changes
    window.addEventListener('beforeunload', () => {
      websocketService.disconnect();
    });

    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (!websocketService.isConnected) {
          websocketService.connect().catch(console.error);
        }
        websocketService.updatePresence('online').catch(console.error);
      } else {
        websocketService.updatePresence('away').catch(console.error);
      }
    });

  } catch (error) {
    console.error('Failed to initialize WebSocket:', error);
  }
};

export default websocketService;